import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { FaCog, FaEdit, FaTrash } from 'react-icons/fa';
import {
  Button,
  Col,
  PopoverBody,
  PopoverHeader,
  Row,
  Table,
  UncontrolledPopover
} from 'reactstrap';
import AuthRequired from '../components/AuthRequired';
import { OrderIcon, ShowCheckBox } from '../components/Common';
import ERChargeModal from '../components/ERChargeModal';
import Loader from '../components/Loader';
import SelectSiteCode from '../components/SelectSiteCode';
import SyncWithLT from '../components/SyncWithLT';
import { GET_ER_CHARGES, SOFT_DELETE_ER_CHARGES } from '../queries';
import { handleExportCSV, sortTable } from '../utilities/erCharges';
import { showConfirmation } from '../ConfirmationDialogService';
import { displaySuccess, displayError } from '../utilities';

const styles = {
  page: {
    margin: '1rem'
  },
  toolbar: {
    marginBottom: '1rem'
  }
};

const ERCharges = () => {
  const [currentEditCharge, setCharge] = useState();
  const [searchCriteria, setSearchCriteria] = useState({});
  const [sorting, setSorting] = useState({ column: 'name', order: 'asc' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteCompleted, setDeleteCompleted] = useState(false);

  const { loading, error, data } = useQuery(GET_ER_CHARGES, {
    variables: { siteCode: searchCriteria.siteCode, name: searchCriteria.name },
    skip: !searchCriteria.siteCode
  });

  const [softDeleteErCharges] = useMutation(SOFT_DELETE_ER_CHARGES, {
    refetchQueries: [
      {
        query: GET_ER_CHARGES,
        variables: {
          siteCode: searchCriteria.siteCode,
          name: searchCriteria.name
        }
      }
    ]
  });

  const hideModal = () => setCharge(undefined);
  const editCharge = charge => {
    setCharge(charge);
  };

  useEffect(() => {
    if (deleteCompleted) {
      setSelectedRows([]);
      setDeleteCompleted(false);
    }
  }, [deleteCompleted]);

  const isSelected = rowData => {
    return selectedRows.some(row => row.id === rowData.id);
  };

  const toggleRowSelection = rowData => {
    if (isSelected(rowData)) {
      setSelectedRows(selectedRows.filter(row => row.id !== rowData.id));
    } else {
      setSelectedRows([...selectedRows, rowData]);
    }
  };

  const handleSorting = column => {
    return () => {
      setSorting(prev => ({
        ...prev,
        column,
        order: prev.order === 'asc' ? 'desc' : 'asc'
      }));
    };
  };

  const handleSingleDelete = (id, name) => {
    const confirmMessage = (
      <>
        <span>
          Permanently delete the ER Charge of{' '}
          <strong style={{ color: 'red' }}>{name}</strong>
          ?<br />
          <br />
          <strong>THIS PROCESS IS IRREVERSIBLE!</strong>
        </span>
      </>
    );

    showConfirmation('Delete ER Charge?', confirmMessage, async () => {
      try {
        await softDeleteErCharges({ variables: { ids: [id] } });
        displaySuccess(`${name} is deleted`);
        setDeleteCompleted(true);
      } catch (err) {
        displayError(err);
      }
    });
  };

  const handleBulkDelete = () => {
    const selectedIds = selectedRows.map(row => row.id);
    const confirmMessage = (
      <>
        <span>
          Permanently delete the selected{' '}
          <strong style={{ color: 'red' }}>{selectedIds.length}</strong> ER
          Charges?
          <br />
          <br />
          <strong>THIS PROCESS IS IRREVERSIBLE!</strong>
        </span>
      </>
    );

    showConfirmation('Delete ER Charges?', confirmMessage, async () => {
      try {
        await softDeleteErCharges({ variables: { ids: selectedIds } });
        displaySuccess(`${selectedIds.length} ER Charges deleted`);
        setDeleteCompleted(true);
      } catch (err) {
        displayError(err);
      }
    });
  };

  const handleSaveCharge = () => {
    hideModal();
  };

  const finalData = data && data.getErCharges ? data.getErCharges : [];

  return (
    <AuthRequired pageName="er-charges">
      <div style={styles.page}>
        <Row>
          <Col sm={3}>
            <h1>ER Charges</h1>
          </Col>
          <Col className="text-right">
            <Button color="link" id="erChargesSettings">
              <FaCog />
            </Button>
            <UncontrolledPopover
              trigger="legacy"
              placement="bottom"
              target="erChargesSettings"
            >
              <PopoverHeader>ER Charges Settings</PopoverHeader>
              <PopoverBody>
                <SyncWithLT syncType="er-charges" displayName="ER Charges" />
              </PopoverBody>
            </UncontrolledPopover>
          </Col>
        </Row>
        <div style={styles.toolbar}>
          <Formik
            initialValues={searchCriteria}
            validate={values => {
              const errors = {};
              if (!values.siteCode) {
                errors.siteCode = 'Required';
              }
              return errors;
            }}
            onSubmit={values => {
              const { name, siteCode } = values;
              const searchC = {};
              if (name && name.trim().length > 3) {
                searchC.name = name;
              }
              if (siteCode && siteCode.length > 0) {
                searchC.siteCode = siteCode;
              }
              setSearchCriteria(searchC);
            }}
            render={({ values }) => (
              <Form>
                <div className="labScanHeader">
                  <label>Name</label>
                  <label>
                    Site Code
                    <span style={{ color: 'red' }}>*</span>
                  </label>
                  <label>&nbsp;</label>
                  <Field name="name" />
                  <Field
                    name="siteCode"
                    render={({ field, form }) => (
                      <SelectSiteCode
                        selectedSiteCode={field.value}
                        onSiteCodeSelect={v =>
                          form.setFieldValue(field.name, v)
                        }
                      />
                    )}
                  />

                  <Button
                    color="primary"
                    type="submit"
                    disabled={!values.siteCode}
                  >
                    Search
                  </Button>
                </div>
              </Form>
            )}
          />
        </div>
        {!searchCriteria.siteCode ? (
          <h3>Please select Hospital</h3>
        ) : loading ? (
          <Loader />
        ) : error ? (
          <div>Error: {error.message}</div>
        ) : finalData.length === 0 ? (
          <h3>No ER charges data Available</h3>
        ) : (
          <>
            <Button
              style={{
                float: 'right',
                color: 'black',
                backgroundColor: 'khaki',
                fontWeight: 'bold',
                marginRight: '2rem',
                marginBottom: '1rem'
              }}
              size="sm"
              onClick={() => handleExportCSV(finalData)}
            >
              Export as CSV
            </Button>
            {selectedRows.length > 0 && (
              <div>
                {selectedRows.length === 1
                  ? '1 entry selected - '
                  : `${selectedRows.length} entries selected - `}
                <Button
                  color="link"
                  style={{ color: 'red' }}
                  onClick={handleBulkDelete}
                >
                  <FaTrash />
                </Button>
              </div>
            )}
            <div>
              <Table size="sm" striped responsive>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={() => {
                          if (selectedRows.length === finalData.length) {
                            setSelectedRows([]);
                          } else {
                            setSelectedRows([...finalData]);
                          }
                        }}
                        checked={selectedRows.length === finalData.length}
                      />
                    </th>
                    <th onClick={handleSorting('name')}>
                      Name{' '}
                      <span style={{ color: 'grey' }}>
                        <OrderIcon {...sorting} sortCol="name" />
                      </span>
                    </th>
                    <th onClick={handleSorting('isEnabled')}>
                      Enabled
                      <span style={{ color: 'grey' }}>
                        <OrderIcon {...sorting} sortCol="isEnabled" />
                      </span>
                    </th>
                    <th onClick={handleSorting('amount')}>
                      Amount{' '}
                      <span style={{ color: 'grey' }}>
                        <OrderIcon {...sorting} sortCol="amount" />
                      </span>
                    </th>
                    <th onClick={handleSorting('itemGroup')}>
                      Group{' '}
                      <span style={{ color: 'grey' }}>
                        <OrderIcon {...sorting} sortCol="itemGroup" />
                      </span>
                    </th>
                    <th onClick={handleSorting('siteCode')}>
                      SiteCode{' '}
                      <span style={{ color: 'grey' }}>
                        <OrderIcon {...sorting} sortCol="siteCode" />
                      </span>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortTable(sorting, finalData).map(data => {
                    return (
                      <tr key={data.id}>
                        <td>
                          <input
                            type="checkbox"
                            onChange={() => toggleRowSelection(data)}
                            checked={isSelected(data)}
                          />
                        </td>
                        <td>{data.name}</td>
                        <td>
                          <ShowCheckBox enable={data.isEnabled} />
                        </td>
                        <td>{data.amount}</td>
                        <td>{data.itemGroup}</td>
                        <td>{data.siteCode}</td>
                        <td>
                          <Button color="link" onClick={() => editCharge(data)}>
                            <FaEdit />
                          </Button>
                          <Button
                            color="link"
                            style={{ color: 'red' }}
                            onClick={() =>
                              handleSingleDelete(data.id, data.name)
                            }
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
        <ERChargeModal
          isOpen={!!currentEditCharge}
          hideModal={hideModal}
          chargeDetails={currentEditCharge}
          onSave={handleSaveCharge}
        />
      </div>
    </AuthRequired>
  );
};

export default ERCharges;
